import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Link,
  Stack,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import AuthenticationRequired from '../components/AuthenticationRequired';
import { useAuth } from '../services/AuthService';
import { useNavigationUtils } from '../utils/navigationUtils';
import JobService from '../services/JobService';
import CompanyProfileService from '../services/CompanyProfileService';
import { usePostHog } from '@posthog/react';

const SimpleJobPost = () => {
  const { user, isAuthenticated } = useAuth();
  const { navigateBackToDashboard } = useNavigationUtils();
  const posthog = usePostHog();
  const [loading, setLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    phone: '',
    referenceCode: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasPhoneInDescription, setHasPhoneInDescription] = useState(false);
  const heroChips = [
    'Duties',
    'Location',
    'Rate',
    'Date',
    'Hours',
    'Requirements',
    'ABN',
    'Good English',
    'Full PPE',
    'White Card',
    "Drivers license",
    'Own Transport',
    'Forklift Ticket'
  ];

  const descriptionRef = useRef(null);

  // Templates to insert when a chip is clicked. Short, copyable snippets.
  const templateMap = {
    'Location': '📍 Location: ',
    'Rate': '💰 Rate /hour: ',
    'Date': '📆 Start: ',
    'Hours': '⏰ Hours: ',
    'Duties': '🛠 Duties: ',
    'Requirements': '📌 Requirements: ',
    'ABN': '- Must have ABN',
    'Full PPE': '- Full PPE',
    'White Card': '- White Card',
    'Forklift Ticket': '- Forklift ticket',
    "Drivers license": '- Drivers license',
    'Own Transport': '- Own Transport',
    'Good English':  '- Good English',
  };

  // Phone detection regex (no global flag) — used to show a privacy warning when description contains phones
  const PHONE_REGEX = /(?:\+?\d{1,3}[ \-.]?)?(?:\(?\d{2,4}\)?[ \-.]?)?\d{3,4}(?:[ \-.]?\d{3,4})+/;

  const onChipClick = (chip) => {
    const insert = templateMap[chip] || '🔖' + ' ';
    setFormData(prev => {
      // Append template on same line and keep cursor at end
      const base = prev.description ? prev.description.trimEnd() + ' ' : '';
      const newDesc = `${base}\n${insert}`;
      // update phone-detection flag as well
      setHasPhoneInDescription(PHONE_REGEX.test(newDesc));
      return { ...prev, description: newDesc };
    });
    // Focus and move cursor to end
    setTimeout(() => {
      const el = descriptionRef.current;
      if (el && typeof el.focus === 'function') {
        el.focus();
        try {
          const len = el.value ? el.value.length : 0;
          el.setSelectionRange(len, len);
        } catch (e) {
          // ignore if not supported
        }
      }
    }, 0);
  };

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      if (!isAuthenticated || !user) return;
      
      try {
        const companyProfileService = new CompanyProfileService(user);
        const profile = await companyProfileService.getRecordsByUserId(user.sub);
        
        if (profile) {
          setCompanyProfile(profile);
          setFormData(prev => ({
            ...prev,
            phone: profile.companyPhone || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching company profile:', err);
      }
    };

    fetchCompanyProfile();
  }, [isAuthenticated, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'description') {
      // Detect common phone number patterns in the description
      setHasPhoneInDescription(PHONE_REGEX.test(value))
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!formData.description.trim()) {
      setError('Job description is required');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('WhatsApp phone number is required');
      return;
    }

    setLoading(true);
    
    try {
      const jobService = new JobService(user);
      
      const jobData = {
        description: formData.description,
        phone: formData.phone,
        ...(formData.referenceCode.trim() && { referenceCode: formData.referenceCode.trim() })
      };
      
      const createdJob = await jobService.createJob(jobData);

      // Notification is handled server-side; no client call required.

      posthog?.capture('job_posted', { job_id: createdJob?.id, origin: 'webapp' });
      setSuccess('Job posted successfully!');
      
      // Reset form
      setFormData({
        description: '',
        phone: companyProfile?.companyPhone || '',
        referenceCode: ''
      });
      
      // Navigate back to dashboard after a short delay
      setTimeout(() => {
        navigateBackToDashboard();
      }, 3000);
      
    } catch (err) {
      console.error('Error creating job:', err);
      // Prefer an explicit API error message when available. `apiClient` attaches parsed
      // response JSON to `error.body`, so check that first. Fall back to generic error.
      let apiError = 'Job posting is disabled for your company.';
      
      if (err?.status === 409 && err?.body?.message) {
        // Duplicate job detected - show the helpful message with retry guidance
        apiError = err.body.message;
      } else if (err?.body?.message) {
        apiError = err.body.message;
      }
      
      setError(apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigateBackToDashboard();
  };

  const handleBack = () => {
    navigateBackToDashboard();
  };

  if (!isAuthenticated) {
    return (
        <AuthenticationRequired 
          message="You need to be logged in to post job opportunities. Join thousands of employers finding great talent!"
          benefits="✓ Free to join • ✓ Easy setup • ✓ Start posting in minutes"
        />
    );
  }

  return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          py: { xs: 1.5, md: 2.5 },
          px: 2
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={{ xs: 1, md: 2 }}>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              variant="text"
              color="inherit"
              sx={{
                alignSelf: { xs: 'stretch', md: 'flex-start' },
                justifyContent: 'flex-start',
                width: 'fit-content',
                mb: 0,
                py: 0,
                fontSize: '0.95rem'
              }}
            >
              Back to dashboard
            </Button>

            {/* Title moved into the form below to keep header compact */}

            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper'
              }}
            >
              <Stack spacing={3}>
                <Stack spacing={1} alignItems="center">
                  <Typography variant="h5" component="h1" fontWeight={700}>
                    Post a Job
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drop a concise brief and confirm the WhatsApp contact — applicants will reach out directly.
                  </Typography>
                </Stack>
                {error && (
                  <Alert severity="error">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 auto' }}>{error}</Box>
                      <Box sx={{ flex: '0 0 auto' }}>
                        <Link href="https://labourandopartner.lovable.app" target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
                          Click here to become a Labourando Partner!
                        </Link>
                      </Box>
                    </Box>
                  </Alert>
                )}

                {success && (
                  <Alert severity="success">
                    {success}
                  </Alert>
                )}

                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Job story
                  </Typography>
                  <TextField
                    name="description"
                    label="Job description"
                    multiline
                      rows={9}
                    value={formData.description}
                    onChange={handleInputChange}
                    inputRef={descriptionRef}
                    placeholder="Describe the role, core tasks, must-have experience, shift patterns, or pay details."
                    required
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.input', '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : undefined, borderWidth: 1.5 }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.78)' : undefined } }}
                  />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, justifyContent: 'flex-start', alignItems: 'center' }}>
                      {heroChips.map((chip) => (
                        <Chip
                          key={chip}
                          label={chip}
                          variant="filled"
                          color="success"
                          size="small"
                          onClick={() => onChipClick(chip)}
                          clickable
                          sx={{ mr: 0.5, fontWeight: 600, border: '1px solid', borderColor: 'success.light', boxShadow: (t) => t.palette.mode === 'dark' ? '0 0 8px hsla(145,72%,40%,0.35)' : 'none' }}
                        />
                      ))}
                    </Box>
                    {hasPhoneInDescription && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Phone numbers in the job description will be removed for privacy — please add a WhatsApp contact in the "WhatsApp phone number" field below so applicants can reach you.
                      </Alert>
                    )}
                </Stack>

               

                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    WhatsApp Contact Details
                  </Typography>
                  <TextField
                    name="phone"
                    label="WhatsApp Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g., +61 400 000 000"
                    required
                    fullWidth
                    variant="outlined"
                    helperText="Candidates will contact you via WhatsApp on this number"
                    sx={{ bgcolor: 'background.input', '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : undefined, borderWidth: 1.5 }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : undefined } }}
                  />
                </Stack>

                 <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Internal Job Id/Reference (optional)
                  </Typography>
                  <TextField
                    name="referenceCode"
                    label="Your internal Job reference"
                    value={formData.referenceCode}
                    onChange={handleInputChange}
                    placeholder="e.g. WH-SYD-001"
                    fullWidth
                    variant="outlined"
                    helperText="Leave blank and we'll generate one automatically (e.g. JOB-A3F2). It helps you to keep track of the jobs published and applications received." 
                    sx={{ bgcolor: 'background.input', '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : undefined, borderWidth: 1.5 }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : undefined } }}
                  />
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleCancel}
                    disabled={loading}
                    sx={{ minWidth: 140 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ minWidth: 160 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Publish Job'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
  );
};

export default SimpleJobPost;